import { Facility } from '@reliance/types/models/Facility';
import { Visitation } from '@reliance/types/models/Visitation';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../format/DateTime';
import { useVisitations, type VisitationTableResult } from '../hooks/Visitation';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import BaseHtmlTable from '../table/BaseHtmlTable';
import { useServerTableFilterState } from '../table/useServerTableFilterState';

const COLUMNS = [
  { id: 'createdDate' },
  { id: 'startDate' },
  { id: 'timeLimit' },
  { id: 'status' },
  { id: 'inmate' },
  { id: 'outsideParty' },
  { id: 'facility' },
];

const columnHelper = createColumnHelper<VisitationTableResult>();

type FacilityVisitsTabProps = {
  facility: Facility;
};

const FacilityVisitsTab: React.FC<FacilityVisitsTabProps> = ({ facility }) => {
  const facilityId = facility.id;

  const baseRecord = useMemo(() => ({}), []);
  const tableState = useServerTableFilterState(COLUMNS, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: 25 },
    initialSorting: [{ id: 'startDate', desc: true }],
  });

  const filters = useMemo(
    () => ({
      page: tableState.mergedRecord.page as number,
      perPage: tableState.mergedRecord.perPage as number,
      orderBy: String(tableState.mergedRecord.orderBy ?? '-startDate'),
    }),
    [tableState.mergedRecord]
  );

  const visitationsResult = useVisitations(facilityId, filters);

  useNotifyTabLoading('visits', visitationsResult.loading);

  const rows = visitationsResult.result?.data ?? [];
  const total = visitationsResult.result?.total ?? 0;
  const page = visitationsResult.result?.page ?? tableState.pagination.pageIndex + 1;
  const perPage = visitationsResult.result?.perPage ?? tableState.pagination.pageSize;

  const columnDefs = useMemo(
    () => [
      columnHelper.accessor((row) => formatDate(row.createdDate), {
        id: 'createdDate',
        header: () => 'Created',
        enableSorting: true,
      }),
      columnHelper.accessor((row) => formatDate(row.startDate), {
        id: 'startDate',
        header: () => 'Start Date',
        enableSorting: true,
      }),
      columnHelper.accessor('timeLimit', {
        header: () => 'Time Limit',
        cell: (info) => `${info.getValue() as number} minutes`,
      }),
      columnHelper.accessor('status', { header: () => 'Status', enableSorting: true }),
      columnHelper.display({
        id: 'inmate',
        header: () => 'Inmate',
        cell: ({ row }) => {
          const inmate = row.original.inmate as Visitation['inmate'];
          if (!inmate?.id) {
            return '';
          }
          return (
            <Link to={`/inmates/view/${inmate.id}`}>
              {inmate.firstName} {inmate.lastName} ({inmate.account})
            </Link>
          );
        },
      }),
      columnHelper.display({
        id: 'outsideParty',
        header: () => 'Outside Party',
        cell: ({ row }) => {
          const op = row.original.outsideParty;
          if (!op) {
            return '';
          }
          return (
            <>
              {op.firstName} {op.lastName}
            </>
          );
        },
      }),
      columnHelper.display({
        id: 'facility',
        header: () => 'Facility',
        cell: ({ row }) => {
          const f = row.original.facility;
          if (!f?.name) {
            return '';
          }
          return (
            <>
              {f.name}
              <br />
              <small>
                <em>
                  {f.city}, {f.state}
                </em>
              </small>
            </>
          );
        },
      }),
    ],
    []
  );

  return (
    <div className="jarviswidget jarviswidget-color-blueDark">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <BaseHtmlTable
              columnDefs={columnDefs}
              loading={visitationsResult.loading}
              page={page}
              paginationMode="server"
              perPage={perPage}
              rows={rows}
              serverPagination={tableState.serverPagination}
              onServerPaginationChange={tableState.onServerPaginationChange}
              serverSorting={tableState.serverSorting}
              onServerSortingChange={tableState.onServerSortingChange}
              serverColumnFilters={tableState.serverColumnFilters}
              onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
              total={total}
              totalPages={Math.ceil(total / perPage) || 1}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilityVisitsTab;
